<template>
  <b-container fluid>
    <!-- Telemetry Card -->
    <b-row class="mb-4">
      <b-col>
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0"><translate>Telemetry</translate></h6>
            <div class="text-right">
              <h5 class="mb-0 text-primary">{{ calculateAICost().formatted }} USD</h5>
              <small class="text-muted">
                Input: {{ calculateAICost().breakdown.inputCost }} | Output:
                {{ calculateAICost().breakdown.outputCost }}
              </small>
            </div>
          </div>
          <pre class="mb-0" v-html="formatJson(telemetry.usageMetadata)"></pre>
        </b-card>
      </b-col>
    </b-row>

    <!-- Control Forms -->
    <b-row class="mb-4">
      <b-col>
        <b-card :header="$gettext('Process Single Address')" header-tag="h5">
          <b-form @submit.prevent="handleSingleAddress">
            <b-form-group
              :label="$gettext('Enter address to process:')"
              label-for="single-address-input"
              :description="$gettext('Enter a single address to test the cleaning and validation process')"
            >
              <b-input-group>
                <b-form-input
                  id="single-address-input"
                  v-model="singleAddressInput"
                  :disabled="processingStep !== null"
                  :placeholder="$gettext('e.g. Av. Corrientes 1234, Buenos Aires, Argentina')"
                  required
                ></b-form-input>
                <b-input-group-append>
                  <b-button type="submit" variant="primary" :disabled="!singleAddressInput || processingStep !== null">
                    <b-spinner v-if="processingStep === 'single'" small></b-spinner>
                    {{ processingStep === 'single' ? $gettext('Processing...') : $gettext('Process Single') }}
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-form>

          <!-- Single Address Processing Steps Indicator -->
          <div v-if="processingStep === 'single'" class="mt-3 mb-3">
            <b-progress :value="singleProgress" :max="100" show-progress animated></b-progress>
            <small class="text-muted mt-1 d-block">{{ singleProgressText }}</small>
          </div>

          <!-- Single Address Result -->
          <div v-if="singleAddressResult" class="mt-3">
            <b-alert show variant="success" class="mb-2">
              <strong><translate>Processing completed!</translate></strong>
            </b-alert>
            <b-table :items="[singleAddressResult]" :fields="fields" small striped responsive>
              <template #cell(confidence)="{ value }">
                <b-badge :variant="getConfidenceBadgeVariant(value)"> {{ value }}% </b-badge>
              </template>

              <template #cell(autocomplete)="{ value }">
                <ul class="mb-0 small">
                  <li v-for="(item, key) in formatAddressResultList(value)" :key="key">
                    {{ item }}
                  </li>
                </ul>
              </template>

              <template #cell(orderedAutocomplete)="{ value }">
                <ul class="mb-0 small">
                  <li v-for="(item, key) in formatAddressResultList(value)" :key="key">
                    {{ item }}
                  </li>
                </ul>
              </template>
            </b-table>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Control Forms -->
    <b-row class="mb-4">
      <b-col>
        <b-card :header="$gettext('Batch Processing')" header-tag="h5">
          <p class="card-text"><translate>Process multiple addresses from shipping data</translate></p>
          <b-button
            @click="handleBatchProcessing"
            variant="success"
            size="lg"
            :disabled="processingStep !== null"
            block
          >
            <b-spinner v-if="processingStep === 'batch'" small></b-spinner>
            {{ processingStep === 'batch' ? $gettext('Processing Batch...') : $gettext('Start Batch Processing') }}
          </b-button>

          <!-- Processing Steps Indicator -->
          <div v-if="processingStep === 'batch'" class="mt-3">
            <b-progress :value="batchProgress" :max="100" show-progress animated></b-progress>
            <small class="text-muted mt-1 d-block">{{ batchProgressText }}</small>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Statistics Cards -->
    <b-row class="mb-4">
      <b-col cols="12">
        <h2><translate>Total Red Addresses:</translate> {{ addresses.length }}</h2>
      </b-col>
      <b-col v-for="stat in statistics" :key="stat.key">
        <b-card>
          <h3 :class="stat.colorClass">{{ stat.count }}</h3>
          {{ stat.label }}
        </b-card>
      </b-col>
    </b-row>

    <!-- Addresses Table -->
    <b-table :busy="!done" :items="addresses" :fields="fields" responsive>
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong><translate>Working...</translate></strong>
        </div>
      </template>

      <template #cell(confidence)="{ value }">
        <b-badge :variant="getConfidenceBadgeVariant(value)"> {{ value }}% </b-badge>
      </template>

      <template #cell(autocomplete)="{ value }">
        <ul class="mb-0">
          <li v-for="(item, key) in formatAddressResultList(value)" :key="key">
            <span v-html="formatJson(item)"></span>
          </li>
        </ul>
      </template>

      <template #cell(orderedAutocomplete)="{ value }">
        <ul class="mb-0">
          <li v-for="(item, key) in formatAddressResultList(value)" :key="key">
            <span v-html="formatJson(item)"></span>
          </li>
        </ul>
      </template>
    </b-table>
  </b-container>
</template>

<script>
/**
 * AI Address Reviewer Component
 *
 * This component provides AI-powered address cleaning, validation, and standardization
 * using Google Gemini models. Features include:
 * - Single address processing with real-time feedback
 * - Batch processing of multiple addresses from shipping data
 * - Integration with Google Places API for validation
 * - Optional Pinit AI enhancement for Mexico addresses
 * - Real-time telemetry and cost tracking
 */

import AddressMixins from '@/mixins/address.mixin.js'
import AddressProcessingMixin from '@/mixins/address-processing.mixin.js'
import AiService from '@/services/ai-service'
import AiTelemetryMixin from '@/mixins/ai-telemetry.mixin'
import AiUtilsMixin from '@/mixins/ai-utils.mixin'

export default {
  mixins: [AddressMixins, AddressProcessingMixin, AiTelemetryMixin, AiUtilsMixin],

  data() {
    return {
      aiConfig: {
        aiVersion: 'v2.0',
        MODELS: {
          CLEAN: 'gemini-2.5-flash',
          CONFIDENCE: 'gemini-2.5-flash-lite',
          FINAL_CONFIDENCE: 'gemini-2.5-flash-lite'
        },
        batch: {
          size: 10,
          delay: 100
        },
        thresholds: {
          pinitActivation: 75,
          pinitCoordinates: 0.85,
          pinitLastResource: 79
        },
        pinit: {
          enabled: false,
          validation: true,
          geocoding: true
        }
      },
      addresses: [],
      telemetry: null,
      fields: ['original', 'clean', 'autocomplete', 'orderedAutocomplete', 'gplaces', 'confidence', 'engine'],
      done: true,
      aiService: null,
      singleAddressInput: '',
      // singleAddressResult, processingStep, singleProgress, singleProgressText removed (now in mixin)
      batchProgress: 0,
      batchProgressText: '',
      aiAddress: {}
    }
  },

  computed: {
    statistics() {
      if (!this.addresses.length) return []

      const errorCount = this.addresses.filter(x => x.clean === 'ERROR').length
      const gplacesErrorCount = this.addresses.filter(x => x.gplaces === 'FAIL').length
      const acceptableCount = this.addresses.filter(x => x.confidence > 75).length

      return [
        {
          key: 'errors',
          count: errorCount,
          label: this.$gettext('Catastrofic cleaning errors (no street and number found)'),
          colorClass: 'text-danger'
        },
        {
          key: 'gplaces_errors',
          count: gplacesErrorCount,
          label: this.$gettext('Gplaces errors'),
          colorClass: 'text-danger'
        },
        {
          key: 'acceptable',
          count: acceptableCount,
          label: this.$gettext('Acceptable changes (>75%)'),
          colorClass: 'text-success'
        }
      ]
    }
  },

  async mounted() {
    this.aiConfig = this.deepMerge(this.aiConfig, this.$frontConfig.ai?.checkAddress?.config || {})
    this.aiConfig.pinit.enabled = false
    this.aiService = new AiService()
    this.telemetry = this.createInitialTelemetry(this.aiConfig.MODELS)
  },

  methods: {
    /**
     * Main workflow for processing all addresses
     * Executes: fetch → clean → autocomplete → calculate confidence
     */
    async processAddresses() {
      try {
        this.done = false
        await this.fetchShippings()
        await this.cleanAddresses()
        await this.delay(this.aiConfig.batch.delay)
        await this.autocompleteAddresses()
        await this.delay(this.aiConfig.batch.delay)
        await this.setConfidences()
      } catch (error) {
        console.error('Error processing addresses:', error)
      } finally {
        this.done = true
      }
    },

    /**
     * Fetches shipping records from API for batch processing
     * Filters for non-outsourced shipments with field revision
     */
    async fetchShippings() {
      const params = {
        sort: [{ key: 'updated_at', direction: 'asc' }],
        limit: 10,
        page: 10,
        filter_groups: [
          {
            filters: [
              ['is_outsourced', 'eq', 'false'],
              ['fields_revision', 'eq', '1'],
              [
                'updated_at',
                'bt',
                [
                  this.moment.utc().subtract(7, 'days').format('YYYY-MM-DD HH:mm:ss'),
                  this.moment.utc().format('YYYY-MM-DD HH:mm:ss')
                ]
              ],
              ['from_country_iso_code', 'eq', 'AR']
            ]
          }
        ]
      }

      const response = await this.$http.get(`${this.$appConfig.api.baseUrl}shippings`, { params })

      this.addresses = response.body.data
        .filter(x => x.fields_revision_data.to && !Array.isArray(x.fields_revision_data.to))
        .map(x => x.fields_revision_data.to)
        .filter(x => !x.includes('SN'))
        .map(x => ({ original: x, aiVersion: this.aiConfig.aiVersion }))
    },

    /**
     * Cleans all addresses in batches using AI
     * Processes in parallel within each batch
     */
    async cleanAddresses() {
      const batches = this.createBatches(this.addresses, this.aiConfig.batch.size)

      for (const batch of batches) {
        const promises = batch.map((address, index) => this.cleanAddress(address.original, address.index))
        await Promise.all(promises)
        await this.delay(this.aiConfig.batch.delay)
      }
    },

    /**
     * Cleans a single address using AI
     * @param {string} address - Raw address string
     * @param {number} index - Address index in array
     */
    async cleanAddress(address, index) {
      try {
        const response = await this.aiService.sendMessage(this.aiConfig.MODELS.CLEAN, this.PROMPTS.CLEAN, address, 0)

        this.updateTelemetry(this.aiConfig.MODELS.CLEAN, response.usageMetadata)

        const parsed = this.parseToonResponse(response.text)

        if (parsed) {
          if (!parsed.data && parsed.address) {
            this.cleanAddress(address)
          } else if ((parsed.error && parsed.error.length !== 0) || parsed.data.length === 0) {
            this.$set(this.addresses[index], 'clean', 'ERROR')
            this.$set(this.addresses[index], 'cleanOptions', [])
          } else {
            const sortedOptions = parsed.data.sort((a, b) => b.confidence - a.confidence)
            this.$set(this.addresses[index], 'clean', sortedOptions[0].guess)
            this.$set(this.addresses[index], 'cleanOptions', sortedOptions)
          }
        } else {
          this.$set(this.addresses[index], 'clean', 'ERROR')
          this.$set(this.addresses[index], 'cleanOptions', [])
        }
      } catch (error) {
        console.error('Error cleaning address:', error)
        this.$set(this.addresses[index], 'clean', 'ERROR')
        this.$set(this.addresses[index], 'cleanOptions', [])
      }
    },

    /**
     * Processes autocomplete for all addresses in parallel
     */
    async autocompleteAddresses() {
      const promises = this.addresses.map((address, index) => this.autocompleteAddress(address.clean, index))
      await Promise.all(promises)
    },

    /**
     * Performs Google Places autocomplete for a single address
     * Tries all clean options and optionally uses Pinit for improvement
     * @param {string} address - Cleaned address string
     * @param {number} index - Address index in array
     */
    async autocompleteAddress(address, index) {
      if (address === 'ERROR') {
        this.markAddressAsFailed(this.addresses[index])
        return
      }

      try {
        const cleanOptions = this.addresses[index].cleanOptions || [{ guess: address, confidence: 100 }]
        const allAutocompleteResults = []

        for (const cleanOption of cleanOptions) {
          const encodedAddress = this.encodeAddressForAPI(cleanOption.guess)
          const response = await this.$http.get(`${this.$appConfig.api.baseUrl}autocomplete?query=${encodedAddress}`)
          allAutocompleteResults.push(...response.body)
        }

        if (allAutocompleteResults.length === 0) {
          if (this.usePinit) {
            const result = await this.tryPinitImprovement(
              this.addresses[index].original,
              cleanOptions[0].guess,
              'batch'
            )

            if (result.success) {
              await this.applyPinitResult(this.addresses[index], result, 'batch')
              return
            }
          }

          this.markAddressAsFailed(this.addresses[index])
          return
        }

        const results = allAutocompleteResults.map(
          x => x.main_text + (x.secondary_text !== '' ? ', ' + x.secondary_text : '')
        )

        this.$set(this.addresses[index], 'autocomplete', { res: results })
        this.$set(this.addresses[index], 'orderedAutocomplete', { res: results })

        if (allAutocompleteResults.length === 1) {
          await this.setSinglePlaceResult(allAutocompleteResults, index)
        } else {
          const bestCleanAddress = cleanOptions[0].guess
          await this.setMultiplePlaceResults(bestCleanAddress, allAutocompleteResults, index)
        }
      } catch (error) {
        console.error('Error in autocomplete:', error)
        this.markAddressAsFailed(this.addresses[index])
      }
    },

    /**
     * Handles single autocomplete result validation
     * @param {Array} places - Array with single place result
     * @param {number} index - Address index in array
     */
    async setSinglePlaceResult(places, index) {
      const placeResult = await this.checkPlaces(places)

      if (!placeResult || !placeResult.formatted_address || !placeResult.is_valid) {
        if (this.usePinit) {
          const cleanAddress = this.addresses[index].clean
          const result = await this.tryPinitImprovement(this.addresses[index].original, cleanAddress, 'batch')

          if (result.success) {
            await this.applyPinitResult(this.addresses[index], result, 'batch')
            return
          }
        }

        this.$set(this.addresses[index], 'place', null)
        this.$set(this.addresses[index], 'gplaces', 'FAIL')
        return
      }

      this.$set(this.addresses[index], 'place', placeResult)
      this.$set(this.addresses[index], 'gplaces', placeResult.formatted_address)
    },

    /**
     * Handles multiple autocomplete results by ranking them by confidence
     * @param {string} cleanAddress - Cleaned address for comparison
     * @param {Array} places - Array of place results
     * @param {number} index - Address index in array
     */
    async setMultiplePlaceResults(cleanAddress, places, index) {
      let confidences = await this.setAcConfidences(cleanAddress, places)
      confidences = this.normalizeConfidences(confidences)
      console.log('CONFIDENCES', confidences)

      const sortedConfidences = confidences?.sort((a, b) => parseInt(b.confidence) - parseInt(a.confidence))
      console.log('SORTED CONFIDENCES', sortedConfidences)

      if (this.usePinit) {
        const result = await this.tryPinitImprovement(this.addresses[index].original, cleanAddress, 'batch')

        if (result.success) {
          await this.applyPinitResult(this.addresses[index], result, 'batch')
          return
        }
      }

      this.$set(this.addresses[index], 'orderedAutocomplete', {
        res: sortedConfidences.map(
          x => `${x.main_text}${x.secondary_text !== '' ? ', ' + x.secondary_text : ''} (${x.confidence}%)`
        )
      })

      const placeResult = await this.checkPlaces(sortedConfidences)
      this.$set(this.addresses[index], 'place', placeResult)
      this.$set(this.addresses[index], 'gplaces', placeResult ? placeResult.formatted_address : 'FAIL')
    },

    /**
     * Calculates final confidence scores for all addresses
     * Uses Pinit score when available, otherwise calculates with AI
     */
    async setConfidences() {
      const promises = this.addresses.map(async (address, index) => {
        if (address.pinitImproved && address.pinitResult?.geocoding?.score) {
          const pinitConfidence = Math.round(address.pinitResult.geocoding.score * 100)

          // If we already have both scores, use the minimum
          if (address.geminiConfidence !== undefined && address.pinitConfidence !== undefined) {
            const finalConfidence = Math.min(address.geminiConfidence, address.pinitConfidence)
            this.$set(this.addresses[index], 'confidence', finalConfidence)
            this.$set(this.addresses[index], 'engine', 'Pinit')
            console.log(
              `Using lower confidence score for Pinit (batch): ${finalConfidence} (Gemini: ${address.geminiConfidence}, Pinit: ${address.pinitConfidence})`
            )
            return
          }

          // If we don't have Gemini confidence, calculate it first
          if (address.clean !== 'ERROR' && address.gplaces !== 'FAIL') {
            try {
              const addressString = `${address.original}|${address.clean}|${address.gplaces}`
              const response = await this.aiService.sendMessage(
                this.aiConfig.MODELS.FINAL_CONFIDENCE,
                this.PROMPTS.CONFIDENCE,
                addressString,
                0
              )
              const geminiConfidence = this.extractConfidenceScore(response.text)

              // Now use MIN of both scores
              const finalConfidence = Math.min(geminiConfidence, pinitConfidence)
              this.$set(this.addresses[index], 'confidence', finalConfidence)
              this.$set(this.addresses[index], 'geminiConfidence', geminiConfidence)
              this.$set(this.addresses[index], 'pinitConfidence', pinitConfidence)
              this.$set(this.addresses[index], 'engine', 'Pinit')
              console.log(
                `Using lower confidence score for Pinit (batch): ${finalConfidence} (Gemini: ${geminiConfidence}, Pinit: ${pinitConfidence})`
              )
              return
            } catch (error) {
              console.error('Error calculating Gemini confidence for Pinit result:', error)
              // Fallback to Pinit score only if Gemini fails
              this.$set(this.addresses[index], 'confidence', pinitConfidence)
              this.$set(this.addresses[index], 'engine', 'Pinit')
              return
            }
          } else {
            // If clean or gplaces failed, use Pinit score only
            this.$set(this.addresses[index], 'confidence', pinitConfidence)
            this.$set(this.addresses[index], 'engine', 'Pinit')
            return
          }
        }

        if (address.clean !== 'ERROR' && address.gplaces !== 'FAIL') {
          return this.setConfidence(`${address.original}|${address.clean}|${address.gplaces}`, index)
        } else {
          this.$set(this.addresses[index], 'confidence', 0)
          this.$set(this.addresses[index], 'engine', 'Error')
          return Promise.resolve()
        }
      })
      await Promise.all(promises)
    },

    /**
     * Calculates confidence score for a single address
     * @param {string} addressString - Combined clean and gplaces address
     * @param {number} index - Address index in array
     */
    async setConfidence(addressString, index) {
      try {
        const response = await this.aiService.sendMessage(
          this.aiConfig.MODELS.FINAL_CONFIDENCE,
          this.PROMPTS.CONFIDENCE,
          addressString,
          0
        )

        this.updateTelemetry(this.aiConfig.MODELS.FINAL_CONFIDENCE, response.usageMetadata)

        const parsed = this.parseToonResponse(response.text)
        const confidenceScore = parsed?.confidence || 0
        this.$set(this.addresses[index], 'confidence', confidenceScore)
        this.$set(this.addresses[index], 'engine', 'Gemini')

        // If confidence is 70 or less and Pinit is available, try improvement
        if (confidenceScore <= this.aiConfig.thresholds.pinitLastResource && this.usePinit) {
          const addressObj = this.addresses[index]
          const bestCleanAddress = addressObj.cleanOptions?.[0]?.guess || addressObj.clean
          console.log(`Low confidence score (${confidenceScore}) for batch address, trying Pinit improvement...`)

          const result = await this.tryPinitImprovement(addressObj.original, bestCleanAddress, 'batch')

          if (result.success) {
            await this.applyPinitResult(this.addresses[index], result, 'batch')
            console.log('Batch address improved with Pinit after low confidence score')
          }
        }
      } catch (error) {
        console.error('Error setting confidence:', error)
        this.$set(this.addresses[index], 'confidence', 0)
        this.$set(this.addresses[index], 'engine', 'Error')
      }
    },

    /**
     * Handles single address form submission
     * Processes address and displays results with progress
     */
    async handleSingleAddress() {
      if (!this.singleAddressInput.trim()) return

      this.processingStep = 'single'
      this.singleAddressResult = null
      this.singleProgress = 0
      this.singleProgressText = this.$gettext('Starting address processing...')

      try {
        this.singleProgress = 10
        this.singleProgressText = this.$gettext('Cleaning address with AI...')

        const result = await this.processSingleAddressWithProgress(this.singleAddressInput.trim())
        this.singleAddressResult = result

        this.singleProgress = 100
        this.singleProgressText = this.$gettext('Processing completed successfully!')

        this.makeToast(
          this.$gettext('Success'),
          this.$gettext('Address processed successfully!'),
          'success',
          false,
          3000
        )

        console.log('Single address result:', result)
      } catch (error) {
        console.error('Error processing single address:', error)

        this.singleProgressText = this.$gettext('Processing failed!')

        this.makeToast(
          this.$gettext('Error'),
          error.message || this.$gettext('Failed to process address'),
          'danger',
          false,
          5000
        )
      } finally {
        // Ocultar la barra de progreso después de un delay
        setTimeout(() => {
          this.processingStep = null
        }, 1500)
      }
    },

    /**
     * Handles batch processing of multiple addresses
     * Fetches shipping data and processes all addresses
     */
    async handleBatchProcessing() {
      this.processingStep = 'batch'
      this.batchProgress = 0
      this.batchProgressText = this.$gettext('Starting batch processing...')
      this.done = false
      this.addresses = []

      try {
        // Fetch shippings
        this.batchProgressText = this.$gettext('Fetching shipping data...')
        this.batchProgress = 10
        await this.fetchShippings()

        // Clean addresses
        this.batchProgressText = this.$gettext('Cleaning addresses...')
        this.batchProgress = 30
        await this.cleanAddresses()

        // Delay
        this.batchProgressText = this.$gettext('Processing autocomplete...')
        this.batchProgress = 50
        await this.delay(this.aiConfig.batch.delay)

        // Autocomplete addresses
        await this.autocompleteAddresses()

        // Delay
        this.batchProgressText = this.$gettext('Calculating confidence scores...')
        this.batchProgress = 80
        await this.delay(this.aiConfig.batch.delay)

        // Set confidences
        await this.setConfidences()

        this.batchProgress = 100
        this.batchProgressText = this.$gettext('Batch processing completed!')

        this.makeToast(
          this.$gettext('Batch Processing Complete'),
          this.$gettext('Successfully processed %{ count } addresses', { count: this.addresses.length }),
          'success',
          false,
          5000
        )
      } catch (error) {
        console.error('Error in batch processing:', error)

        this.makeToast(
          this.$gettext('Batch Processing Error'),
          error.message || this.$gettext('Batch processing failed'),
          'danger',
          false,
          5000
        )
      } finally {
        this.processingStep = null
        this.done = true
      }
    },

    /**
     * Helper: Renders address list for table cells
     * @param {Object} value - Address result value
     * @returns {Array} Array of address strings
     */
    formatAddressResultList(value) {
      return value?.res || []
    },

    /**
     * Resets the single address form and results
     */
    resetSingleAddressForm() {
      this.singleAddressInput = ''
      this.singleAddressResult = null
    },

    /**
     * Clears all address data and resets telemetry
     */
    clearAllData() {
      this.addresses = []
      this.singleAddressResult = null
      this.telemetry = this.createInitialTelemetry(this.aiConfig.MODELS)
      this.done = false
    }
  }
}
</script>

<style lang="scss" scoped>
.mb-0 {
  margin-bottom: 0 !important;
}

.small {
  font-size: 0.875rem;
}

.card-text {
  color: #6c757d;
}

pre {
  font-size: 0.8rem;
  max-height: 200px;
  overflow-y: auto;
}
</style>
